import { useMemo } from "react";
import { makeStyles, shorthands, mergeClasses } from "@fluentui/react-components";
import { BRAND } from "../risikoanalyseStep/tokens";
import { useSharedStyles } from "./sharedStyles";
import {
  anyVersichert,
  formatThousands,
  labelForGefahrId,
  mietverlustGefahrIds,
  onlyDigits,
  type GefahrenFormState,
} from "./gefahrenConfig";

// ── shared tile (local copy to keep file standalone) ─────────────
const useTileStyles = makeStyles({
  tile: {
    cursor: "pointer",
    appearance: "none",
    textAlign: "left",
    backgroundColor: BRAND.white,
    backgroundImage: "none",
    color: BRAND.ink,
    fontFamily: BRAND.fontPrimary,
    paddingTop: "14px",
    paddingBottom: "14px",
    paddingLeft: "16px",
    paddingRight: "16px",
    ...shorthands.borderRadius("10px"),
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
    display: "flex",
    flexDirection: "column",
    rowGap: "6px",
    minHeight: "70px",
    transitionProperty: "border-color, background-color, box-shadow",
    transitionDuration: "150ms",
    ":hover": {
      ...shorthands.borderColor(BRAND.purple),
      backgroundColor: "#f5eef6",
    },
  },
  tileActive: {
    ...shorthands.border("2px", "dashed", BRAND.purple),
    backgroundColor: "#f5eef6",
    boxShadow: "0 4px 14px rgba(77,32,81,0.18)",
  },
  title: { fontWeight: 700, fontSize: "0.95rem", color: BRAND.ink },
  hint: { fontSize: "0.78rem", color: BRAND.muted },
});

const Tile = ({
  active,
  title,
  hint,
  onClick,
}: {
  active: boolean;
  title: string;
  hint?: string;
  onClick: () => void;
}) => {
  const styles = useTileStyles();
  return (
    <button
      type="button"
      className={mergeClasses(styles.tile, active && styles.tileActive)}
      onClick={onClick}
    >
      <span className={styles.title}>{title}</span>
      {hint && <span className={styles.hint}>{hint}</span>}
    </button>
  );
};

// ── VersicherungssummeBlock ─────────────────────────────────
const useVsStyles = makeStyles({
  panel: {
    backgroundColor: BRAND.white,
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
    ...shorthands.borderRadius("10px"),
    paddingTop: "16px",
    paddingBottom: "16px",
    paddingLeft: "18px",
    paddingRight: "18px",
    display: "flex",
    flexDirection: "column",
    rowGap: "12px",
  },
  panelTitle: {
    fontFamily: BRAND.fontAccent,
    fontSize: "1.1rem",
    color: BRAND.ink,
    margin: 0,
  },
  threeRow: {
    display: "grid",
    gridTemplateColumns: "1fr",
    gap: "10px",
    "@media (min-width: 720px)": { gridTemplateColumns: "1fr 1fr 1fr" },
  },
  computed: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "1.05rem",
    fontWeight: 700,
    color: BRAND.ink,
    paddingTop: "9px",
    paddingBottom: "9px",
    paddingLeft: "12px",
    paddingRight: "12px",
    backgroundColor: "#f5eef6",
    ...shorthands.borderRadius("6px"),
    ...shorthands.border("1px", "dashed", BRAND.purple),
  },
  haftzeitGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
    gap: "10px",
  },
  removeBtn: {
    appearance: "none",
    cursor: "pointer",
    backgroundColor: "transparent",
    backgroundImage: "none",
    color: "#a1331c",
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.85rem",
    fontWeight: 600,
    ...shorthands.border("0"),
    textDecorationLine: "underline",
  },
  addBtn: {
    appearance: "none",
    cursor: "pointer",
    backgroundColor: BRAND.bg,
    backgroundImage: "none",
    color: BRAND.ink,
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.85rem",
    fontWeight: 600,
    paddingTop: "8px",
    paddingBottom: "8px",
    paddingLeft: "14px",
    paddingRight: "14px",
    alignSelf: "flex-start",
    ...shorthands.borderRadius("999px"),
    ...shorthands.border("1px", "dashed", BRAND.purple),
    ":hover": { backgroundColor: "#f5eef6" },
  },
});

export type VsAnteil = { bezeichnung: string; vs: string; basis: "1914" | "1980" };

export const VersicherungssummeBlock = ({
  wert1914,
  baupreisindex,
  baupreisindexJahr,
  wert1980,
  wertzuschlag,
  wertzuschlagJahr,
  anteile,
  onChange,
  onChangeAnteile,
}: {
  wert1914: string;
  baupreisindex: string;
  baupreisindexJahr: string;
  wert1980: string;
  wertzuschlag: string;
  wertzuschlagJahr: string;
  anteile: VsAnteil[];
  onChange: (
    k: "wert1914" | "baupreisindex" | "baupreisindexJahr" | "wert1980" | "wertzuschlag" | "wertzuschlagJahr",
    v: string,
  ) => void;
  onChangeAnteile: (next: VsAnteil[]) => void;
}) => {
  const card = useSharedStyles();
  const styles = useVsStyles();
  const summe1914 = useMemo(() => {
    const w = parseFloat(wert1914.replace(",", "."));
    const i = parseFloat(baupreisindex.replace(",", "."));
    if (!Number.isFinite(w) || !Number.isFinite(i)) return "";
    return ((w * i) / 100).toFixed(2);
  }, [wert1914, baupreisindex]);
  const summe1980 = useMemo(() => {
    const w = parseFloat(wert1980.replace(",", "."));
    const z = parseFloat(wertzuschlag.replace(",", "."));
    if (!Number.isFinite(w)) return "";
    const factor = Number.isFinite(z) ? 1 + z / 100 : 1;
    return (w * factor).toFixed(2);
  }, [wert1980, wertzuschlag]);

  const updateAnteil = (idx: number, patch: Partial<VsAnteil>) => {
    const next = anteile.map((a, i) => (i === idx ? { ...a, ...patch } : a));
    onChangeAnteile(next);
  };

  return (
    <div className={card.cardSection}>
      <div className={card.helper}>
        Versicherungssumme des gesamten Gebäudekomplexes des Risikoortes.
      </div>
      <div className={styles.panel}>
        <h4 className={styles.panelTitle}>Wert 1914 Mark (100%)</h4>
        <div className={styles.threeRow}>
          <div className={card.field}>
            <label className={card.label}>Wert 1914 (Mark)</label>
            <input
              className={card.input}
              type="number"
              value={wert1914}
              onChange={(e) => onChange("wert1914", e.target.value)}
              placeholder="0"
            />
          </div>
          <div className={card.field}>
            <label className={card.label}>Baupreisindex</label>
            <input
              className={card.input}
              type="number"
              value={baupreisindex}
              onChange={(e) => onChange("baupreisindex", e.target.value)}
              placeholder="z. B. 1850"
            />
            <div className={card.field} style={{ marginTop: 6 }}>
              <label className={card.label}>Jahr</label>
              <input
                className={card.input}
                type="number"
                value={baupreisindexJahr}
                onChange={(e) => onChange("baupreisindexJahr", e.target.value)}
                placeholder="2024"
              />
            </div>
          </div>
          <div className={card.field}>
            <label className={card.label}>Summe €</label>
            <div className={styles.computed}>
              {summe1914 ? Number(summe1914).toLocaleString("de-DE", { minimumFractionDigits: 2 }) : "—"}
            </div>
            <span className={card.helper}>= Wert 1914 × Baupreisindex / 100</span>
          </div>
        </div>
      </div>

      <div className={styles.panel}>
        <h4 className={styles.panelTitle}>Wert 1980 € (100%)</h4>
        <div className={styles.threeRow}>
          <div className={card.field}>
            <label className={card.label}>Wert 1980 (€)</label>
            <input
              className={card.input}
              type="number"
              value={wert1980}
              onChange={(e) => onChange("wert1980", e.target.value)}
              placeholder="0"
            />
          </div>
          <div className={card.field}>
            <label className={card.label}>zzgl. Wertzuschlag (%)</label>
            <input
              className={card.input}
              type="number"
              value={wertzuschlag}
              onChange={(e) => onChange("wertzuschlag", e.target.value)}
              placeholder="z. B. 22"
            />
            <div className={card.field} style={{ marginTop: 6 }}>
              <label className={card.label}>Jahr</label>
              <input
                className={card.input}
                type="number"
                value={wertzuschlagJahr}
                onChange={(e) => onChange("wertzuschlagJahr", e.target.value)}
                placeholder="2024"
              />
            </div>
          </div>
          <div className={card.field}>
            <label className={card.label}>Summe €</label>
            <div className={styles.computed}>
              {summe1980 ? Number(summe1980).toLocaleString("de-DE", { minimumFractionDigits: 2 }) : "—"}
            </div>
            <span className={card.helper}>= Wert 1980 × (1 + Wertzuschlag / 100)</span>
          </div>
        </div>
      </div>

      <div className={styles.panel}>
        <h4 className={styles.panelTitle}>Anteilige Versicherungssummen einzelner Gebäude</h4>
        <div className={card.helper}>
          Optional, wenn am Risikoort mehrere Gebäudekomplexteile vorhanden sind.
        </div>
        {anteile.map((a, idx) => (
          <div key={idx} className={styles.threeRow}>
            <div className={card.field}>
              <label className={card.label}>Bezeichnung</label>
              <input
                className={card.input}
                type="text"
                value={a.bezeichnung}
                onChange={(e) => updateAnteil(idx, { bezeichnung: e.target.value })}
                placeholder="Gebäudekomplexteil"
              />
            </div>
            <div className={card.field}>
              <label className={card.label}>Anteilige VS (€)</label>
              <input
                className={card.input}
                type="number"
                value={a.vs}
                onChange={(e) => updateAnteil(idx, { vs: e.target.value })}
                placeholder="0"
              />
            </div>
            <div className={card.field}>
              <label className={card.label}>Basis</label>
              <select
                className={card.input}
                value={a.basis}
                onChange={(e) => updateAnteil(idx, { basis: e.target.value as "1914" | "1980" })}
              >
                <option value="1914">Wert 1914</option>
                <option value="1980">Wert 1980</option>
              </select>
              <button
                type="button"
                className={styles.removeBtn}
                style={{ marginTop: 6, alignSelf: "flex-start" }}
                onClick={() => onChangeAnteile(anteile.filter((_, i) => i !== idx))}
              >
                Entfernen
              </button>
            </div>
          </div>
        ))}
        <button
          type="button"
          className={styles.addBtn}
          onClick={() =>
            onChangeAnteile([...anteile, { bezeichnung: "", vs: "", basis: "1914" }])
          }
        >
          + Gebäudekomplexteil hinzufügen
        </button>
      </div>
    </div>
  );
};

// ── UVV Picker (Vereinbart Ja/Nein → optional Variante) ───────────────
export type UvvVariante = "" | "courte" | "versicherer" | "gutachten" | "sonstiges";

export type UvvState = {
  vereinbart: "ja" | "nein" | "";
  variante: UvvVariante;
  sonstigesText: string;
};

export const UVV_VARIANTE_OPTIONS: Array<{ key: Exclude<UvvVariante, "">; label: string }> = [
  { key: "courte", label: "gemäß Gebäudewertermittlungsbogen Courté" },
  { key: "versicherer", label: "gemäß Gebäudewertermittlung Versicherer" },
  { key: "gutachten", label: "gemäß Gutachten" },
  { key: "sonstiges", label: "Sonstiges (Freitext)" },
];

export const UvvPicker = ({
  state,
  onPatch,
}: {
  state: UvvState;
  onPatch: (patch: Partial<UvvState>) => void;
}) => {
  const card = useSharedStyles();
  return (
    <div className={card.cardSection}>
      <div className={card.helper}>
        Ist ein Unterversicherungsverzicht vereinbart? Bei „Ja" bitte die Grundlage angeben.
      </div>
      <div className={card.tileGrid2}>
        <Tile
          active={state.vereinbart === "ja"}
          title="Vereinbart"
          onClick={() => onPatch({ vereinbart: state.vereinbart === "ja" ? "" : "ja" })}
        />
        <Tile
          active={state.vereinbart === "nein"}
          title="Nicht vereinbart"
          onClick={() =>
            onPatch({
              vereinbart: state.vereinbart === "nein" ? "" : "nein",
              variante: "",
              sonstigesText: "",
            })
          }
        />
      </div>

      {state.vereinbart === "ja" && (
        <div className={card.cardSection}>
          <div className={card.tileGrid2}>
            {UVV_VARIANTE_OPTIONS.map((o) => (
              <Tile
                key={o.key}
                active={state.variante === o.key}
                title={o.label}
                onClick={() =>
                  onPatch({
                    variante: state.variante === o.key ? "" : o.key,
                    sonstigesText: o.key === "sonstiges" ? state.sonstigesText : "",
                  })
                }
              />
            ))}
          </div>
          {state.variante === "sonstiges" && (
            <div className={card.field} style={{ maxWidth: 560 }}>
              <label className={card.label}>Sonstige Grundlage</label>
              <textarea
                className={card.textarea}
                value={state.sonstigesText}
                onChange={(e) => onPatch({ sonstigesText: e.target.value })}
                placeholder="Bitte näher beschreiben…"
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ── MietverlustBlock ────────────────────────────────────────
// Erscheint erst, wenn in der Gefahren-Tabelle mindestens einmal Mietverlust
// angekreuzt wurde. Haftzeit und Höhe gelten dann für alle diese Gefahren.
export const HAFTZEITEN = [12, 18, 24, 36, 48];

export type MietverlustState = {
  /** Haftzeit in Monaten — gilt für alle Gefahren mit Mietverlust. */
  haftzeit: string;
  /** "20pct" = 20 % der Versicherungssumme, "brutto" = Bruttojahresmietwert. */
  vsArt: string;
  /** Bruttojahresmietwert in € (nur bei vsArt === "brutto"). */
  vsBrutto: string;
  temporaer: string;
  temporaerWelche: string;
  ausschluss: string;
};

/** Betragsfeld in € mit Tausenderpunkten; im State stehen reine Ziffern. */
const EuroFeld = ({
  value,
  onChange,
  placeholder = "0",
}: {
  value: string;
  onChange: (next: string) => void;
  placeholder?: string;
}) => {
  const card = useSharedStyles();
  return (
    <div className={card.inputWithSuffix}>
      <input
        className={card.input}
        type="text"
        inputMode="numeric"
        autoComplete="off"
        value={formatThousands(value ?? "")}
        onChange={(e) => onChange(onlyDigits(e.target.value))}
        placeholder={placeholder}
        style={{ maxWidth: 220, textAlign: "right" }}
      />
      <span className={card.inlineSuffix}>€</span>
    </div>
  );
};

export const MietverlustBlock = ({
  state,
  gefahren,
  onPatch,
}: {
  state: MietverlustState;
  gefahren: GefahrenFormState;
  onPatch: (patch: Partial<MietverlustState>) => void;
}) => {
  const card = useSharedStyles();
  const styles = useVsStyles();
  const mvIds = useMemo(() => mietverlustGefahrIds(gefahren), [gefahren]);

  return (
    <div className={card.cardSection}>
      <div className={styles.panel}>
        <h4 className={styles.panelTitle}>Mietverlust — Haftzeit</h4>
        <div className={card.helper}>
          {mvIds.length > 0
            ? `Gilt für ${mvIds.length} Gefahr${mvIds.length === 1 ? "" : "en"} mit vereinbartem Mietverlust: ${mvIds
                .map((id) => labelForGefahrId(id))
                .join(", ")}.`
            : "Bitte kreuzen Sie in der Tabelle zuerst mindestens einmal Mietverlust an."}
        </div>
        <div className={styles.haftzeitGrid}>
          {HAFTZEITEN.map((h) => (
            <Tile
              key={h}
              active={state.haftzeit === String(h)}
              title={`${h} Monate`}
              onClick={() => onPatch({ haftzeit: state.haftzeit === String(h) ? "" : String(h) })}
            />
          ))}
        </div>
      </div>

      <div className={styles.panel}>
        <h4 className={styles.panelTitle}>Mietverlust — Höhe</h4>
        <div className={card.helper}>Versicherungssumme für den Mietverlust.</div>
        <div className={card.tileGrid2}>
          <Tile
            active={state.vsArt === "20pct"}
            title="20 % der Versicherungssumme pro Versicherungsjahr"
            onClick={() => onPatch({ vsArt: state.vsArt === "20pct" ? "" : "20pct", vsBrutto: "" })}
          />
          <Tile
            active={state.vsArt === "brutto"}
            title="Bruttojahresmietwert pro Versicherungsjahr"
            onClick={() => onPatch({ vsArt: state.vsArt === "brutto" ? "" : "brutto" })}
          />
        </div>
        {state.vsArt === "brutto" && (
          <div className={card.field} style={{ maxWidth: 320 }}>
            <label className={card.label}>Bruttojahresmietwert (€)</label>
            <EuroFeld value={state.vsBrutto} onChange={(v) => onPatch({ vsBrutto: v })} />
          </div>
        )}
      </div>

      <div className={styles.panel}>
        <h4 className={styles.panelTitle}>Temporär vermietete Risiken / Objekte</h4>
        <div className={card.tileGrid2}>
          <Tile active={state.temporaer === "ja"} title="Ja" onClick={() => onPatch({ temporaer: "ja" })} />
          <Tile
            active={state.temporaer === "nein"}
            title="Nein"
            onClick={() => onPatch({ temporaer: "nein", temporaerWelche: "", ausschluss: "" })}
          />
        </div>
        {state.temporaer === "ja" && (
          <>
            <div className={card.field}>
              <label className={card.label}>Welches Risiko / Bezeichnung</label>
              <input
                className={card.input}
                type="text"
                value={state.temporaerWelche}
                onChange={(e) => onPatch({ temporaerWelche: e.target.value })}
              />
            </div>
            <div className={card.tileGrid2}>
              <Tile
                active={state.ausschluss === "teil"}
                title="Teilausschluss Mietverlust"
                hint="Deckung über separate Ertragsausfallversicherung."
                onClick={() => onPatch({ ausschluss: "teil" })}
              />
              <Tile
                active={state.ausschluss === "komplett"}
                title="Komplettausschluss Mietverlust"
                hint="Deckung über separate Ertragsausfallversicherung."
                onClick={() => onPatch({ ausschluss: "komplett" })}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

/** Haftzeit-Auswahl als Dropdown — für die Vorvertrags-Angabe beim Fremdvertrag. */
export const HaftzeitSelect = ({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}) => {
  const card = useSharedStyles();
  return (
    <select
      className={card.input}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      style={{ maxWidth: 220 }}
    >
      <option value="">Haftzeit wählen…</option>
      {HAFTZEITEN.map((h) => (
        <option key={h} value={String(h)}>
          {h} Monate
        </option>
      ))}
    </select>
  );
};

// ── GefahrenTabelle — re-exported from GefahrenTabelle.tsx ─────────────
export { GefahrenTabelle } from "./GefahrenTabelle";

export { anyVersichert };

// ── BeitragBlock ────────────────────────────────────────
export const BeitragBlock = ({
  premiumAnnualGross,
  vatRate,
  zahlweise,
  onChange,
}: {
  premiumAnnualGross: string;
  vatRate: string;
  zahlweise: string;
  onChange: (k: "premiumAnnualGross" | "vatRate", v: string) => void;
}) => {
  const card = useSharedStyles();
  const styles = useVsStyles();
  const splitFactor: Record<string, number> = {
    jaehrlich: 1,
    halbjaehrlich: 2,
    vierteljaehrlich: 4,
    monatlich: 12,
  };
  const factor = splitFactor[zahlweise] ?? 1;
  const split = useMemo(() => {
    const v = parseFloat(premiumAnnualGross.replace(",", "."));
    if (!Number.isFinite(v)) return "";
    return (v / factor).toLocaleString("de-DE", { minimumFractionDigits: 2 });
  }, [premiumAnnualGross, factor]);

  return (
    <div className={card.cardSection}>
      <div className={styles.panel}>
        <h4 className={styles.panelTitle}>Beitrag Gesamt jährlich</h4>
        <div className={card.helper}>Inkl. gesetzlicher Versicherungssteuer.</div>
        <div className={card.twoCol}>
          <div className={card.field}>
            <label className={card.label}>Brutto-Jahresbeitrag (€)</label>
            <input
              className={card.input}
              type="number"
              value={premiumAnnualGross}
              onChange={(e) => onChange("premiumAnnualGross", e.target.value)}
              placeholder="0,00"
            />
          </div>
          <div className={card.field}>
            <label className={card.label}>Versicherungssteuer (%)</label>
            <input
              className={card.input}
              type="number"
              value={vatRate}
              onChange={(e) => onChange("vatRate", e.target.value)}
              placeholder="19"
            />
          </div>
        </div>
        <div className={card.field}>
          <label className={card.label}>Beitrag pro Zahlweise</label>
          <div className={styles.computed}>{split ? `${split} €` : "—"}</div>
          <span className={card.helper}>
            Aufgeteilt auf {factor === 1 ? "1 Rate (jährlich)" : `${factor} Raten (${zahlweise || "—"})`}.
          </span>
        </div>
      </div>
    </div>
  );
};

// ── TemporaereAusschlussBlock — per temporäres Risiko teil/komplett ─────────────
export type TemporaereAusschlussState = Record<number, "teil" | "komplett" | "">;

export type TemporaereRiskMeta = {
  id: number;
  label: string;
};

export const TemporaereAusschlussBlock = ({
  risks,
  state,
  onPatch,
}: {
  risks: TemporaereRiskMeta[];
  state: TemporaereAusschlussState;
  onPatch: (next: TemporaereAusschlussState) => void;
}) => {
  const card = useSharedStyles();
  const styles = useVsStyles();
  if (!risks.length) return null;
  return (
    <div className={styles.panel}>
      <h4 className={styles.panelTitle}>Temporär vermietete Risiken — Mietverlust-Ausschluss</h4>
      <div className={card.helper}>
        Für jedes temporär vermietete Risiko wählen Sie hier Teilausschluss oder Komplettausschluss des Mietverlusts.
      </div>
      <div className={card.cardSection}>
        {risks.map((r) => {
          const current = state[r.id] ?? "";
          return (
            <div key={r.id} className={card.perGefahrRow}>
              <div className={card.perGefahrTitle}>{r.label}</div>
              <div className={card.tileGrid2} style={{ gridColumn: "span 2" }}>
                <Tile
                  active={current === "teil"}
                  title="Teilausschluss"
                  hint="Mietverlust nur teilweise; Restdeckung über Ertragsausfallversicherung."
                  onClick={() => onPatch({ ...state, [r.id]: current === "teil" ? "" : "teil" })}
                />
                <Tile
                  active={current === "komplett"}
                  title="Komplettausschluss"
                  hint="Mietverlust komplett über Ertragsausfallversicherung."
                  onClick={() => onPatch({ ...state, [r.id]: current === "komplett" ? "" : "komplett" })}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
